import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Lock, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import BadgeArt from '../components/BadgeArt';
import { buildShareImage, downloadBlob } from '../lib/buildShareImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category color accents.
const CATEGORY_COLORS = {
  Progress: '#34C759',
  Milestones: '#8B5A3C',
  Speed: '#FF9F0A',
  Mastery: '#AF52DE',
  Streaks: '#FF3B30',
};

export default function AchievementsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharingId, setSharingId] = useState(null);

  const username = user?.username || user?.email?.split('@')[0] || 'anonymous';

  const handleShare = async (a) => {
    setSharingId(a.id);
    try {
      const blob = await buildShareImage({
        achievementId: a.id,
        name: a.name,
        description: a.description,
        category: a.category,
        username,
      });
      downloadBlob(blob, `sudoku-academy-${a.id}.png`);
      toast.success('Image saved — attach it to your tweet!');
      setTimeout(() => {
        const url = new URL('https://twitter.com/intent/tweet');
        url.searchParams.set(
          'text',
          `I just unlocked ${a.name} on Sudoku Academy — ${a.description}`,
        );
        url.searchParams.set('url', window.location.origin);
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      }, 300);
    } catch (err) {
      toast.error('Could not build share image.');
    } finally {
      setSharingId(null);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data: payload } = await axios.get(`${API_URL}/api/achievements`, {
          withCredentials: true,
        });
        setData(payload);
      } catch (err) {
        toast.error('Failed to load achievements');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const byCategory = (data?.achievements || []).reduce((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Achievements
          </h1>
          <p className="text-[var(--text-secondary)]">
            {user
              ? `You've unlocked ${data?.unlocked_count ?? 0} of ${data?.total ?? 0} badges.`
              : 'Sign in to start earning badges as you play.'}
          </p>
          {!user && (
            <Button
              onClick={() => navigate('/login')}
              className="mt-4 bg-[#8B5A3C] hover:bg-[#A46944] text-white"
              data-testid="achievements-login-cta"
            >
              Sign In to Track Progress
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(byCategory).map(([cat, items]) => {
              const color = CATEGORY_COLORS[cat] || '#8B5A3C';
              return (
                <section key={cat} data-testid={`achievement-category-${cat}`}>
                  <h3
                    className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"
                    style={{ color }}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {cat}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((a) => {
                      const locked = !a.unlocked;
                      return (
                        <Card
                          key={a.id}
                          className={`bg-[var(--background-surface)] border-[var(--border-subtle)] transition-all ${
                            locked ? 'opacity-60' : ''
                          }`}
                          data-testid={`achievement-${a.id}`}
                        >
                          <CardContent className="p-4 flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <BadgeArt
                                achievementId={a.id}
                                category={a.category}
                                locked={locked}
                                size={56}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-heading font-semibold text-[var(--text-primary)] text-sm flex items-center gap-1.5">
                                {a.name}
                                {locked && <Lock className="w-3 h-3 text-[var(--text-disabled)]" />}
                              </h4>
                              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                                {a.description}
                              </p>
                              {a.unlocked_at && (
                                <p className="text-[11px] text-[var(--text-disabled)] mt-1 font-mono">
                                  Unlocked {new Date(a.unlocked_at).toLocaleDateString()}
                                </p>
                              )}
                              {!locked && (
                                <button
                                  type="button"
                                  onClick={() => handleShare(a)}
                                  disabled={sharingId === a.id}
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50"
                                  style={{ color }}
                                  data-testid={`share-${a.id}`}
                                >
                                  <Share2 className="w-3 h-3" />
                                  {sharingId === a.id ? 'Generating…' : 'Share'}
                                </button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
